import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header/Header';
import Navbar from './components/Navbar/Navbar';
import PromoBar from './components/PromoBar/PromoBar';
import HeroCarousel from './components/HeroCarousel/HeroCarousel';
import Categories from './components/Categories/Categories';
import BookDemo from './components/BookDemo/BookDemo';
import RegisterWebinar from './components/RegisterWebinar/RegisterWebinar';
import SchoolStudent from './components/SchoolStudent/SchoolStudent';
import SchoolOrganisation from './components/SchoolOrganisation/SchoolOrganisation';
import TrainingDomains from './components/TrainingDomains/TrainingDomains';
import CourseDetail from './components/CourseDetail/CourseDetail';
import CollegeStudent from './components/CollegeStudent/CollegeStudent';
import CollegeOrganisation from './components/CollegeOrganisation/CollegeOrganisation';
import WhyIbots from './components/WhyIbots/WhyIbots';
import DIYKits from './components/DIYKits/DIYKits';
import TrustedInstitutions from './components/TrustedInstitutions/TrustedInstitutions';
import TrainingGallery from './components/TrainingGallery/TrainingGallery';
import StatsCounter from './components/StatsCounter/StatsCounter';
import OurOffices from './components/OurOffices/OurOffices';
import Footer from './components/Footer/Footer';
import LabSetup from './pages/LabSetup/LabSetup';
import HackathonSupport from './pages/HackathonSupport/HackathonSupport';
import RegisterPage from './pages/RegisterPage/RegisterPage';
import UserDashboard from './pages/UserDashboard/UserDashboard';
import SparkPage from './pages/SparkPage/SparkPage';
import ToTPage from './pages/ToTPage/ToTPage';
import UpdatesPage from './pages/UpdatesPage/UpdatesPage';
import SeasonalPopup from './components/SeasonalPopup/SeasonalPopup';
import Awards from './components/Awards/Awards';
import SuccessStories from './components/SuccessStories/SuccessStories';
import './App.css';

function MainPage() {
  const [showDemo, setShowDemo] = useState(false);
  const [showWebinar, setShowWebinar] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showTraining, setShowTraining] = useState(false);
  const [trainingType, setTrainingType] = useState('regular');
  const [activeCourse, setActiveCourse] = useState(null);
  const schoolStudentRef = useRef(null);
  const schoolOrgRef = useRef(null);
  const trainingRef = useRef(null);
  const courseDetailRef = useRef(null);
  const collegeStudentRef = useRef(null);
  const collegeOrgRef = useRef(null);

  const [showSeasonal, setShowSeasonal] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      fetch('https://ipapi.co/json/')
        .then(r => r.json())
        .then(data => {
          if (data.country_code === 'CA') {
            setShowSeasonal(true);
          }
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const resetAll = () => {
    setShowTraining(false);
    setActiveCourse(null);
    setTrainingType('regular');
  };

  const handleTraining = (type = 'regular') => {
    setTrainingType(type);
    setShowTraining(true);
    setActiveCourse(null);
    setTimeout(() => {
      trainingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleCourseDetail = (courseName) => {
    setActiveCourse(courseName);
    setTimeout(() => {
      courseDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleCloseTraining = () => {
    setShowTraining(false);
    setActiveCourse(null);
  };

  const handleSchoolStudent = () => {
    if (activeCategory === 'school') { setActiveCategory(null); resetAll(); return; }
    resetAll();
    setActiveCategory('school');
    setTimeout(() => { schoolStudentRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const handleSchoolOrg = () => {
    if (activeCategory === 'schoolorg') { setActiveCategory(null); resetAll(); return; }
    resetAll();
    setActiveCategory('schoolorg');
    setTimeout(() => { schoolOrgRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const handleCollegeStudent = () => {
    if (activeCategory === 'college') { setActiveCategory(null); resetAll(); return; }
    resetAll();
    setActiveCategory('college');
    setTrainingType('college');
    setTimeout(() => { collegeStudentRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const handleCollegeOrg = () => {
    if (activeCategory === 'collegeorg') { setActiveCategory(null); resetAll(); return; }
    resetAll();
    setActiveCategory('collegeorg');
    setTimeout(() => { collegeOrgRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  return (
    <div className="app">
      <Header />
      <Navbar />
      <PromoBar />
      <HeroCarousel
        onBookDemo={() => setShowDemo(true)}
        onRegisterWebinar={() => setShowWebinar(true)}
      />
      <div id="categories-section">
        <Categories
          onSchoolStudent={handleSchoolStudent}
          onSchoolOrg={handleSchoolOrg}
          onCollegeStudent={handleCollegeStudent}
          onCollegeOrg={handleCollegeOrg}
          activeCategory={activeCategory}
          onCategoryChange={(cat) => { resetAll(); setActiveCategory(cat); }}
        />
      </div>

      <div ref={schoolStudentRef}>
        {activeCategory === 'school' && (
          <SchoolStudent
            onClose={() => { setActiveCategory(null); setShowTraining(false); setActiveCourse(null); }}
            onMoreDetails={handleTraining}
            onOpenRoadmap={() => { setShowTraining(false); setActiveCourse(null); }}
          />
        )}
      </div>

      <div ref={schoolOrgRef}>
        {activeCategory === 'schoolorg' && (
          <SchoolOrganisation onClose={() => setActiveCategory(null)} />
        )}
      </div>

      <div ref={collegeStudentRef}>
        {activeCategory === 'college' && (
          <CollegeStudent
            onClose={() => { setActiveCategory(null); resetAll(); }}
            onMoreDetails={handleTraining}
            onCloseTraining={() => { setShowTraining(false); setActiveCourse(null); }}
          />
        )}
      </div>

      <div ref={collegeOrgRef}>
        {activeCategory === 'collegeorg' && (
          <CollegeOrganisation
            onClose={() => setActiveCategory(null)}
            onMoreDetails={handleTraining}
            onCloseTraining={() => { setShowTraining(false); setActiveCourse(null); }}
          />
        )}
      </div>

      <div ref={trainingRef}>
        {showTraining && (
          <TrainingDomains
            onClose={() => { setShowTraining(false); setActiveCourse(null); }}
            onMoreDetails={handleCourseDetail}
            type={trainingType}
          />
        )}
      </div>

      <div ref={courseDetailRef}>
        {activeCourse && (
          <CourseDetail
            courseName={activeCourse}
            onClose={() => setActiveCourse(null)}
            trainingType={trainingType}
          />
        )}
      </div>

      <WhyIbots />
      <DIYKits />
      <hr className="section-divider" />
      <TrustedInstitutions />
      <hr className="section-divider" />
      <TrainingGallery />
      <hr className="section-divider" />
      <Awards />
      <hr className="section-divider" />
      <SuccessStories />
      <hr className="section-divider" />
      <StatsCounter />
      <hr className="section-divider" />
      <OurOffices />
      <Footer />

      {showDemo    && <BookDemo onClose={() => setShowDemo(false)} />}
      {showWebinar && <RegisterWebinar onClose={() => setShowWebinar(false)} />}
      {showSeasonal && <SeasonalPopup onClose={() => setShowSeasonal(false)} />}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/"                    element={<MainPage />} />
      <Route path="/lab-setup"           element={<LabSetup />} />
      <Route path="/hackathon-support"   element={<HackathonSupport />} />
      <Route path="/register"            element={<RegisterPage />} />
      <Route path="/my-account"          element={<UserDashboard />} />
      <Route path="/ibots-spark"         element={<SparkPage />} />
      <Route path="/tot"                 element={<ToTPage />} />
      <Route path="/updates"             element={<UpdatesPage />} />
    </Routes>
  );
}

export default App;
